import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationSwitch } from "@/hooks/useOrganizationSwitch";
import { organizationAccessLabel, organizationSwitcherOptions } from "@/lib/organizationSelection";
import { cn } from "@/lib/utils";

/**
 * Header-style organization picker. Hidden unless the user can reach at least
 * two organizations. Picking an org on an org page rewrites the URL's org
 * segment (see `useOrganizationSwitch`). The role badge sits outside the item
 * text, so the option's accessible name and the trigger text stay exactly the
 * organization name, which browser automation selects organizations by.
 */
export function OrganizationSwitcher({ className }: { className?: string }) {
  const { memberships, allOrganizations, isSuperAdmin, selectedOrganization } = useAuth();
  const { switchOrganization, switching } = useOrganizationSwitch();
  const options = useMemo(
    () => organizationSwitcherOptions({ memberships, allOrganizations, isSuperAdmin }),
    [memberships, allOrganizations, isSuperAdmin]
  );

  if (options.length < 2) {
    return null;
  }

  return (
    <Select
      value={selectedOrganization?.id ?? ""}
      disabled={switching}
      onValueChange={(orgId) => {
        const option = options.find((o) => o.organization.id === orgId);
        if (option) {
          void switchOrganization(option.organization);
        }
      }}>
      <SelectTrigger
        aria-label="Organization"
        className={cn("w-[140px] sm:w-[200px] lg:w-[240px]", className)}>
        <SelectValue placeholder="Select organization" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => {
          const label = organizationAccessLabel(option);
          return (
            <SelectItem
              key={option.organization.id}
              value={option.organization.id}
              trailing={label && <Badge variant="secondary">{label}</Badge>}>
              {option.organization.name || "Untitled organization"}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
